import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { requirePermission } from '@/lib/route-guards';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export const Route = createFileRoute('/_authed/fleet/new')({
  beforeLoad: () => requirePermission('vehicles.write'),
  component: FleetNewPage,
});

// Même contrôle que fleet-service (routes/vehicles/imei.ts) : 15 chiffres + chiffre de contrôle Luhn
const isValidImei = (imei: string): boolean => {
  if (!/^\d{15}$/.test(imei)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    let d = Number(imei[14 - i]);
    if (i % 2 === 1) { d *= 2; if (d > 9) d -= 9; }
    sum += d;
  }
  return sum % 10 === 0;
};

// Aligné sur le schema Zod du fleet-service (create.controller.ts)
const schema = z.object({
  plate: z.string().min(3, 'Plaque requise'),
  make: z.string().optional(),
  model: z.string().optional(),
  year: z.coerce.number().int().min(1990).max(2030).optional(),
  fuelType: z.enum(['DIESEL', 'GASOLINE', 'ELECTRIC', 'HYBRID', 'CNG', 'LPG']).optional(),
  deviceImei: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || isValidImei(v), "IMEI invalide : 15 chiffres, celui inscrit sur le boîtier GPS (*#06#)"),
});

type FormData = z.infer<typeof schema>;

function FleetNewPage() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const mutation = useMutation({
    mutationFn: (data: FormData) => api.post('/vehicles', data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success('Véhicule créé avec succès');
      void nav({ to: '/fleet' });
    },
    onError: (err: unknown) => {
      const e = (err as {
        response?: { data?: { error?: { message?: string; details?: { message: string }[] } } };
      })?.response?.data?.error;
      // Erreur de validation : le détail par champ est plus utile que « Validation échouée sur body »
      const msg = e?.details?.length ? e.details.map((d) => d.message).join(' · ') : e?.message;
      toast.error(msg ?? 'Erreur lors de la création');
    },
  });

  return (
    <div className="p-6 space-y-4 max-w-2xl">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/fleet"><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Link>
        </Button>
        <h1 className="text-2xl font-bold">Nouveau véhicule</h1>
      </div>

      <Card>
        <CardHeader><CardTitle>Informations du véhicule</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="plate">Immatriculation *</Label>
                <Input id="plate" {...register('plate')} placeholder="LGT-001-A" />
                {errors.plate && <p className="text-xs text-danger mt-1">{errors.plate.message}</p>}
              </div>
              <div>
                <Label htmlFor="fuelType">Carburant</Label>
                <Select onValueChange={(v) => setValue('fuelType', v as FormData['fuelType'])}>
                  <SelectTrigger id="fuelType"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DIESEL">Diesel</SelectItem>
                    <SelectItem value="GASOLINE">Essence</SelectItem>
                    <SelectItem value="ELECTRIC">Électrique</SelectItem>
                    <SelectItem value="HYBRID">Hybride</SelectItem>
                    <SelectItem value="CNG">GNV</SelectItem>
                    <SelectItem value="LPG">GPL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="make">Marque</Label>
                <Input id="make" {...register('make')} placeholder="Toyota" />
              </div>
              <div>
                <Label htmlFor="model">Modèle</Label>
                <Input id="model" {...register('model')} placeholder="Land Cruiser" />
              </div>
              <div>
                <Label htmlFor="year">Année</Label>
                <Input id="year" type="number" {...register('year')} placeholder="2020" />
              </div>
              <div>
                <Label htmlFor="deviceImei">IMEI tracker (optionnel)</Label>
                <Input id="deviceImei" {...register('deviceImei')} inputMode="numeric" placeholder="864000000000005" />
                {errors.deviceImei && <p className="text-xs text-danger mt-1">{errors.deviceImei.message}</p>}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" type="button" asChild>
                <Link to="/fleet">Annuler</Link>
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Création…' : 'Créer le véhicule'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
